import { Component, Inject, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { L10N_LOCALE, L10nLocale } from 'angular-l10n';
import { MappingProperty, UsersService } from 'src/app/handweave-api';
import { BehaviorSubject, catchError, throwError } from 'rxjs';
import { Metadata } from 'src/app/hickory-api';
import { AuthService } from '../../authentication/auth.service';
import { MatSelectChange } from '@angular/material/select';

@Component({
  selector: 'app-abac-authorization',
  templateUrl: './abac-authorization.component.html',
  styleUrl: './abac-authorization.component.scss',
  standalone: false
})
export class AbacAuthorizationComponent implements OnInit, OnChanges {
  @Input() metadata: Metadata;

  constructor(
    @Inject(L10N_LOCALE) public locale: L10nLocale,
    private usersService: UsersService,
    private authService: AuthService,
  ) { }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.metadata) {
      this.workingAttributeData = this.getTableDataFromMetadata(this.metadata);
      this.dataSource.next(this.workingAttributeData);
    }
  }

  private tenantId: number;

  isAddingAttribute = false;
  availableAttributeKeys: string[] = [];
  availableAttributeValues: string[] = [];

  selectedAttributeKey: string;
  selectedAttributeValues: string[];

  dataSource = new BehaviorSubject<AttributeTableData[]>([]);
  workingAttributeData: AttributeTableData[] = [];

  displayedColumns: string[] = ['key', 'value', 'delete'];

  ngOnInit(): void {
    this.tenantId = this.authService.user.snapshot?.tenantId;
    this.usersService.usersGetFields({ tenantId: this.metadata.tenantId }).subscribe(fields => {
      this.availableAttributeKeys = this.buildFieldPaths(fields.properties);
      // update table data
      this.workingAttributeData = this.getTableDataFromMetadata(this.metadata);
      this.dataSource.next(this.workingAttributeData);
    });
  }

  onAttributeAdd() {
    this.isAddingAttribute = true;
  }

  buildFieldPaths(fields: MappingProperty[], key: string = null, recurse: boolean = false): string[] {
    let pathFields = [];
    const shouldBuildFields = ['addresses', 'emails', 'phoneNumbers', 'address', 'attributes', 'tag', 'facility'];
    const shouldIgnoreFields = ['communicationGroups', 'access', 'membership'];

    // Keep track of deeper nested properties
    const keyPrefix = key ? `${key}.` : '';

    for (const f of fields) {
      let nestedFields: string[] = [];

      if (shouldIgnoreFields.includes(f.name)) {
        continue;
      }
      if (f.properties && recurse) {
        nestedFields = this.buildFieldPaths(f.properties, `${key}.${f.name}`);
      } else if (f.name === 'meta' && f.properties) {
        nestedFields = this.buildFieldPaths(f.properties, `${keyPrefix}meta`, true);
      } else if (shouldBuildFields.includes(f.name) && f.properties) {
        nestedFields = this.buildFieldPaths(f.properties, `${keyPrefix}${f.name}`);
      } else {
        const type = key ? key + '.' + f.name : f.name;
        pathFields.push(type);
      }

      pathFields = pathFields.concat(nestedFields);
    }

    return pathFields;
  }

  onAttributeKeyChange(key: string) {
    this.selectedAttributeKey = key;
    this.usersService.usersGetPossibleValues({
      tenantId: this.tenantId.toString(),
      field: key, complexSearch: [{}]
    }).pipe(
      catchError((err) => {
        this.availableAttributeValues = [];

        return throwError(() => err);
      })
    )
      .subscribe((result) => {
        this.availableAttributeValues = result;
      });
  }

  onAttributeValueChange(key: MatSelectChange) {
    this.selectedAttributeValues = key.value;
  }

  onCustomAttributeValueChange(value: string) {
    this.selectedAttributeValues = value.split(',');
  }
  onAttributeSubmit() {
    // initialize the tag array if it doesn't already exist
    if (!this.metadata.tags[this.selectedAttributeKey]) {
      this.metadata.tags[this.selectedAttributeKey] = [];
    }

    // populate the tag array with selected values
    this.selectedAttributeValues?.forEach((element) => {
      // only add the value to the metadata object if it doesn't already exist on the key
      if (this.metadata.tags[this.selectedAttributeKey].indexOf(element) === -1) {
        this.metadata.tags[this.selectedAttributeKey].push(element);
      }
    });

    // update the table
    this.workingAttributeData = this.getTableDataFromMetadata(this.metadata);
    this.dataSource.next(this.workingAttributeData);

    // clear the form
    this.clearAttributeForm();
  }
  onAttributeCancel() {
    // clear the form
    this.clearAttributeForm();
  }

  onAttributeDelete(key: string) {
    // remove record from metadata object
    delete this.metadata.tags[key];

    // update table data
    this.workingAttributeData = this.getTableDataFromMetadata(this.metadata);
    this.dataSource.next(this.workingAttributeData);
  }

  clearAttributeForm() {
    this.selectedAttributeKey = '';
    this.selectedAttributeValues = [];
    this.isAddingAttribute = false;
  }

  getTableDataFromMetadata(metadata: Metadata): AttributeTableData[] {
    // convert the metadata.tags to a more appropriate table data structure
    const tableData: AttributeTableData[] = [];
    const collectionKeys = ['people-', 'groups-', 'everyone-', 'roles-'];
    for (const property in metadata.tags) {
      const keyPrefix = collectionKeys.find(x => property.startsWith(x));
      if (!keyPrefix) {
        tableData.push({
          key: property,
          values: metadata.tags[property],
        });
      }
    }
    return tableData;
  }

}

interface AttributeTableData {
  key: string,
  values: string[]
}
